import {
    createContext,
    ReactNode,
    useState,
    useEffect,
    useContext,
    Dispatch,
    SetStateAction,
    useCallback,
} from 'react';
import { loyaltyProgramDataKey } from '@/config/axios';
import { useRestaurantContext } from '@/contexts/restaurant';
import { ILoyaltyProgram, LoyaltyProgramType } from '@/interfaces/loyalty/types';
import LoyaltyService from '@/services/loyalty';
import { useUserContext } from '@/contexts/user';
import { restrictRoles } from '@/constants/roleRestrictions';

interface ILoyaltyContext {
    loading: boolean;
    programs: ILoyaltyProgram[];
    setPrograms: Dispatch<SetStateAction<ILoyaltyProgram[]>> | null;
    selectedProgram: ILoyaltyProgram | null;
    setSelectedProgram: ((program: ILoyaltyProgram) => void) | null;
}

function getLocalProgram(): ILoyaltyProgram | null {
    try {
        const item = localStorage.getItem(loyaltyProgramDataKey);
        if (item) {
            return JSON.parse(item);
        }
    } catch {
        return null;
    }
    return null;
}

function setLocalProgram(item: ILoyaltyProgram | null) {
    localStorage.setItem(loyaltyProgramDataKey, JSON.stringify(item));
}

const LoyaltyContext = createContext<ILoyaltyContext>({
    loading: true,
    programs: [],
    setPrograms: null,
    selectedProgram: null,
    setSelectedProgram: null,
});

type Props = {
    children?: ReactNode;
};

export function LoyaltyContextProvider({ children }: Props) {
    const { user } = useUserContext();
    const { restaurant } = useRestaurantContext();
    const loyaltyService = LoyaltyService.getInstance();
    const [loading, setLoading] = useState(true);
    const [programs, setPrograms] = useState<ILoyaltyProgram[]>([]);
    const [selectedProgram, setSelectedProgram] = useState<ILoyaltyProgram | null>(getLocalProgram());

    const setSelectedProgramHandler = useCallback((program: ILoyaltyProgram) => {
        setSelectedProgram(program);
        setLocalProgram(program);
    }, []);

    const resetState = useCallback(() => {
        setPrograms([]);
        setSelectedProgram(null);
        setLocalProgram(null);
    }, []);

    function getLoyaltyPrograms() {
        if (!restaurant?.restaurant_unique || !restrictRoles.LOYALTY.includes(user?.userData?.role)) {
            if (selectedProgram) resetState();
            return;
        }

        setLoading(true);
        loyaltyService
            .getRestaurantEnrolledPrograms({
                restaurantUnique: restaurant.restaurant_unique,
                country: restaurant.country_code,
                enabled: true,
            })
            .then((response) => {
                const list: ILoyaltyProgram[] = [];
                let matched: ILoyaltyProgram | null = null;
                let fallback: ILoyaltyProgram | null = null;
                for (const program of response.programs) {
                    if (program.type !== 'qlub') continue;

                    const mod = {
                        ...program,
                        programType: program.subscription?.options.isActive
                            ? LoyaltyProgramType.IN_SUB_DISCOUNT
                            : LoyaltyProgramType.IN_EARN_BURN,
                    };

                    list.push(mod);

                    if (!matched && selectedProgram?.id === mod.id) {
                        matched = mod;
                    } else if (!fallback) {
                        fallback = mod;
                    }
                }

                const selected = matched || fallback;

                if (selected) {
                    setSelectedProgramHandler(selected);
                    setPrograms(list);
                } else {
                    resetState();
                }
            })
            .catch(() => {
                resetState();
            })
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        if (restaurant?.restaurant_unique) getLoyaltyPrograms();
    }, [restaurant?.restaurant_unique, user?.userData?.role]);

    return (
        <LoyaltyContext.Provider
            // eslint-disable-next-line react/jsx-no-constructed-context-values
            value={{
                loading,
                programs,
                setPrograms,
                selectedProgram,
                setSelectedProgram: setSelectedProgramHandler,
            }}
        >
            {children}
        </LoyaltyContext.Provider>
    );
}

export const useLoyaltyContext = () => useContext(LoyaltyContext);
